import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Close, Delete, ContentCopy, Send, History } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog, EmptyState, FormDialog, InlineMessage, LoadingState, SectionCard, StatusChip } from './ui';
import type { StatusTone } from './ui';
import { useOrgFeatureFlag } from '../hooks/useOrgFeatureFlags';
import { LockedFeature } from './planLock/LockedFeature';
import { usePlanSurface } from './planLock/usePlanSurface';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { formatFirestoreDate } from '../utils/firestoreDate';
import { useToast } from '../contexts/ToastContext';
import { tokens } from '../theme/tokens';

const EVENTS = [
  { key: 'product.created', label: 'Product created' },
  { key: 'product.updated', label: 'Product updated' },
  { key: 'price.changed', label: 'Price changed (needs real-time sync)' },
] as const;

interface WebhookEndpoint {
  id: string;
  url: string;
  events: string[];
  includeMsrp: boolean;
  status: 'active' | 'paused' | 'disabled';
  consecutiveFailures: number;
  lastDeliveryAt: string | null;
  lastError: string | null;
}

interface WebhookDelivery {
  id: string;
  event: string;
  status: 'pending' | 'delivered' | 'dead' | 'skipped';
  attempts: number;
  createdAt: string;
  skippedReason?: string;
  lastStatusCode?: number | null;
  lastError?: string | null;
  isTest?: boolean;
}

const QUERY_KEY = ['api-webhooks'] as const;

const endpointTone: Record<WebhookEndpoint['status'], StatusTone> = {
  active: 'success',
  paused: 'warning',
  disabled: 'error',
};

const deliveryTone: Record<WebhookDelivery['status'], StatusTone> = {
  delivered: 'success',
  pending: 'info',
  skipped: 'neutral',
  dead: 'error',
};

async function callWebhooks<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = await getAuthHeaders();
  const response = await makeApiCall(`/webhooks${path}`, { ...init, headers });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error?.message || body?.message || body?.error || 'Request failed');
  return body as T;
}

/**
 * Outbound catalog webhooks on the API keys page (flag `catalog_webhooks`).
 * Renders nothing while the flag is off for the organization.
 */
export const CatalogWebhooksCard: React.FC = () => {
  const { enabled } = useOrgFeatureFlag('catalog_webhooks');
  const surface = usePlanSurface({ feature: 'customApi', orgFlag: 'catalog_webhooks' });
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['product.created', 'product.updated']);
  const [includeMsrp, setIncludeMsrp] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WebhookEndpoint | null>(null);
  const [logTarget, setLogTarget] = useState<WebhookEndpoint | null>(null);

  const list = useQuery({
    queryKey: QUERY_KEY,
    enabled: enabled && surface.mode === 'open',
    queryFn: async () => (await callWebhooks<{ data: WebhookEndpoint[]; meta?: { max: number } }>('')),
  });

  const deliveries = useQuery({
    queryKey: [...QUERY_KEY, logTarget?.id, 'deliveries'],
    enabled: enabled && surface.mode === 'open' && !!logTarget,
    queryFn: async () => (await callWebhooks<{ data: WebhookDelivery[] }>(`/${logTarget?.id}/deliveries`)).data,
  });

  const create = useMutation({
    mutationFn: async () =>
      callWebhooks<{ data: { secret: string } }>('', {
        method: 'POST',
        body: JSON.stringify({ url: url.trim(), events, includeMsrp }),
      }),
    onSuccess: (result) => {
      setCreateOpen(false);
      setUrl('');
      setNewSecret(result.data.secret);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => callWebhooks(`/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteTarget(null);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  const sendTest = useMutation({
    mutationFn: async (id: string) => callWebhooks<{ data: WebhookDelivery }>(`/${id}/test`, { method: 'POST' }),
    onSuccess: (result) => {
      showToast(
        result.data.status === 'delivered'
          ? 'Test delivered'
          : `Test failed: ${result.data.lastError || 'no response'}`,
        result.data.status === 'delivered' ? 'success' : 'error'
      );
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });

  if (surface.mode === 'locked') {
    return (
      <SectionCard dense title="Webhooks">
        <LockedFeature variant="inline" planName={surface.planName} title="Webhooks" />
      </SectionCard>
    );
  }
  if (!enabled || surface.mode !== 'open') return null;

  const endpoints = list.data?.data ?? [];
  const max = list.data?.meta?.max ?? 5;
  const toggleEvent = (key: string) =>
    setEvents((prev) => (prev.includes(key) ? prev.filter((e) => e !== key) : [...prev, key]));

  return (
    <SectionCard
      dense
      title="Webhooks"
      actions={
        <Button
          size="small"
          startIcon={<Add />}
          disabled={endpoints.length >= max}
          onClick={() => setCreateOpen(true)}
        >
          Add endpoint
        </Button>
      }
      sx={{ mt: 3 }}
    >
      {list.isLoading ? (
        <LoadingState message="Loading webhooks..." />
      ) : list.isError ? (
        <Box sx={{ p: 2 }}>
          <InlineMessage severity="error">Failed to load webhooks.</InlineMessage>
        </Box>
      ) : endpoints.length === 0 ? (
        <EmptyState
          size="compact"
          title="No webhook endpoints"
          description="Get a signed HTTPS call when catalog products or prices change, instead of polling."
        />
      ) : (
        <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line.hairline}` }} />}>
          {endpoints.map((endpoint) => (
            <Stack
              key={endpoint.id}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
              sx={{ px: '20px', py: 1.5 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2" sx={{ wordBreak: 'break-all' }}>
                    {endpoint.url}
                  </Typography>
                  <StatusChip label={endpoint.status} tone={endpointTone[endpoint.status]} />
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div">
                  {endpoint.events.join(', ')}
                  {endpoint.includeMsrp ? ' · includes MSRP' : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="div">
                  Last delivery: {endpoint.lastDeliveryAt ? formatFirestoreDate(endpoint.lastDeliveryAt) : 'never'}
                  {endpoint.lastError ? ` · ${endpoint.lastError}` : ''}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Send test">
                  <span>
                    <IconButton
                      aria-label="Send test"
                      disabled={sendTest.isPending}
                      onClick={() => sendTest.mutate(endpoint.id)}
                    >
                      <Send fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Delivery log">
                  <IconButton aria-label="Delivery log" onClick={() => setLogTarget(endpoint)}>
                    <History fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton aria-label="Delete" onClick={() => setDeleteTarget(endpoint)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}

      <FormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={() => create.mutate()}
        title="Add webhook endpoint"
        submitLabel={create.isPending ? 'Adding...' : 'Add endpoint'}
        submitDisabled={!url.trim() || events.length === 0 || create.isPending}
      >
        <TextField
          autoFocus
          margin="dense"
          label="Endpoint URL"
          placeholder="https://example.com/motzu-webhook"
          fullWidth
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          helperText="HTTPS only."
          sx={{ mt: 1 }}
        />
        <FormGroup sx={{ mt: 1 }}>
          {EVENTS.map((event) => (
            <FormControlLabel
              key={event.key}
              label={event.label}
              control={<Checkbox checked={events.includes(event.key)} onChange={() => toggleEvent(event.key)} />}
            />
          ))}
          <FormControlLabel
            label="Include MSRP in payloads"
            control={<Checkbox checked={includeMsrp} onChange={(e) => setIncludeMsrp(e.target.checked)} />}
          />
        </FormGroup>
      </FormDialog>

      <FormDialog
        open={newSecret !== null}
        onClose={() => setNewSecret(null)}
        onSubmit={() => setNewSecret(null)}
        title="Signing secret"
        submitLabel="Done"
        hideCancel
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          Copy this secret now. It is shown only once and is used to verify the X-Motzu-Signature header.
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField fullWidth size="small" value={newSecret ?? ''} InputProps={{ readOnly: true }} />
          <IconButton
            aria-label="Copy secret"
            onClick={() => {
              navigator.clipboard?.writeText(newSecret ?? '');
              showToast('Secret copied');
            }}
          >
            <ContentCopy fontSize="small" />
          </IconButton>
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete webhook endpoint"
        message="Deliveries to this URL stop immediately. Pending retries are dropped."
        confirmLabel="Delete"
        confirmColor="error"
        loading={remove.isPending}
        onConfirm={() => deleteTarget && remove.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      />

      <Drawer anchor="right" open={logTarget !== null} onClose={() => setLogTarget(null)}>
        <Box sx={{ width: { xs: '100vw', sm: 440 }, p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6">Delivery log</Typography>
            <IconButton aria-label="Close" onClick={() => setLogTarget(null)}>
              <Close />
            </IconButton>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {logTarget?.url} · last 50 deliveries
          </Typography>
          {deliveries.isLoading ? (
            <LoadingState message="Loading deliveries..." />
          ) : (deliveries.data ?? []).length === 0 ? (
            <EmptyState size="compact" title="No deliveries yet" />
          ) : (
            <Stack divider={<Box sx={{ borderTop: `1px solid ${tokens.line.hairline}` }} />} sx={{ mt: 1 }}>
              {(deliveries.data ?? []).map((d) => (
                <Box key={d.id} sx={{ py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {d.event}
                    </Typography>
                    <StatusChip label={d.status} tone={deliveryTone[d.status]} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {formatFirestoreDate(d.createdAt)} · attempts {d.attempts}
                    {d.lastStatusCode ? ` · HTTP ${d.lastStatusCode}` : ''}
                    {d.skippedReason ? ` · skipped: ${d.skippedReason}` : ''}
                    {d.lastError ? ` · ${d.lastError}` : ''}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Drawer>
    </SectionCard>
  );
};
